/**
 * Collections List Page with Drag-and-Drop
 * Main view for data model - lists all collections with folders and drag-and-drop organization
 * Reference: reference/app/src/modules/settings/routes/data-model/collections/collections.vue
 */

import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import {
    Database,
    Search,
    Plus,
    ChevronRight,
    ChevronDown,
    Settings,
    MoreVertical,
    Trash2,
    FolderPlus,
    GripVertical,
    Folder,
    FolderOpen,
    Eye,
    EyeOff,
    FolderEdit,
} from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    DragOverEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useCollections, useDeleteCollection, useUpdateCollection, isSystemCollection } from '@/hooks/useCollections';
import type { Collection } from '@/api/collections';
import { NewCollectionSheet } from './NewCollectionSheet';
import { CreateFolderDialog } from './CreateFolderDialog';
import { RenameFolderDialog } from './RenameFolderDialog';
import { toast } from 'sonner';

// Utility to format collection/folder names (capitalize and replace underscores with spaces)
function formatCollectionName(name: string): string {
    return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function CollectionsPage() {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [showNewCollection, setShowNewCollection] = useState(false);
    const [showFolderDialog, setShowFolderDialog] = useState(false);
    const [showRenameFolderDialog, setShowRenameFolderDialog] = useState(false);
    const [editingFolder, setEditingFolder] = useState<Collection | null>(null);
    const [renamingFolder, setRenamingFolder] = useState<Collection | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Collection | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [activeId, setActiveId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);

    const { data: collections, isLoading } = useCollections();
    const deleteCollection = useDeleteCollection();
    const updateCollection = useUpdateCollection();

    // Configure sensors for drag-and-drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Sort collections by sort field
    const sortedCollections = useMemo(() => {
        if (!collections) return [];
        return [...collections].sort((a, b) => {
            const aSort = a.meta?.sort ?? 999;
            const bSort = b.meta?.sort ?? 999;
            if (aSort !== bSort) return aSort - bSort;
            return a.collection.localeCompare(b.collection);
        });
    }, [collections]);

    // Filter and organize collections
    const { rootCollections, groupedCollections, systemCollections, tableCollections } = useMemo(() => {
        if (!sortedCollections) {
            return { rootCollections: [], groupedCollections: {}, systemCollections: [], tableCollections: [] };
        }

        const filtered = search
            ? sortedCollections.filter((c) =>
                c.collection.toLowerCase().includes(search.toLowerCase()) ||
                (c.meta?.note || '').toLowerCase().includes(search.toLowerCase())
            )
            : sortedCollections;

        const system = filtered.filter((c) => isSystemCollection(c));
        const tables = filtered.filter((c) => !c.meta); // Collections without meta (raw tables)
        const withMeta = filtered.filter((c) => c.meta && !isSystemCollection(c));

        const root = withMeta.filter((c) => !c.meta?.group);
        const grouped: Record<string, Collection[]> = {};

        withMeta
            .filter((c) => c.meta?.group)
            .forEach((c) => {
                const group = c.meta!.group!;
                if (!grouped[group]) grouped[group] = [];
                grouped[group].push(c);
            });

        return {
            rootCollections: root,
            groupedCollections: grouped,
            systemCollections: system,
            tableCollections: tables,
        };
    }, [sortedCollections, search]);

    const handleDelete = () => {
        if (deleteTarget) {
            deleteCollection.mutate(deleteTarget.collection, {
                onSuccess: () => {
                    toast.success('Collection deleted successfully');
                    setDeleteTarget(null);
                },
                onError: () => {
                    toast.error('Failed to delete collection');
                },
            });
        }
    };

    const toggleGroup = (group: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(group)) {
                next.delete(group);
            } else {
                next.add(group);
            }
            return next;
        });
    };

    const handleCollectionCreated = (collectionName: string) => {
        setShowNewCollection(false);
        navigate(`/admin/settings/data-model/${collectionName}`);
    };

    const handleFolderCreated = () => {
        setShowFolderDialog(false);
        setEditingFolder(null);
    };

    const handleEditFolder = (folder: Collection) => {
        setEditingFolder(folder);
        setShowFolderDialog(true);
    };

    const handleRenameFolder = (folder: Collection) => {
        setRenamingFolder(folder);
        setShowRenameFolderDialog(true);
    };

    const handleToggleHidden = async (collection: Collection) => {
        try {
            await updateCollection.mutateAsync({
                collection: collection.collection,
                payload: {
                    ...collection.meta,
                    hidden: !collection.meta?.hidden,
                },
            });
            toast.success(`${collection.schema ? 'Collection' : 'Folder'} ${collection.meta?.hidden ? 'shown' : 'hidden'}`);
        } catch (error) {
            toast.error('Failed to update visibility');
        }
    };

    // Drag and drop handlers
    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragOver = (event: DragOverEvent) => {
        setOverId(event.over?.id as string || null);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);
        setOverId(null);

        if (!over || active.id === over.id) return;

        const activeCollection = sortedCollections.find(c => c.collection === active.id);
        const overCollection = sortedCollections.find(c => c.collection === over.id);

        if (!activeCollection) return;

        // Determine new group and sort order
        let newGroup = activeCollection.meta?.group || null;
        let reorderedCollections = [...rootCollections];

        // If dropping on a folder, set it as parent
        const overIsFolder = overCollection && !overCollection.schema;
        if (overIsFolder) {
            newGroup = overCollection.collection;
            reorderedCollections = groupedCollections[overCollection.collection] || [];
        } else if (overCollection?.meta?.group) {
            // If dropping on an item in a group, join that group
            newGroup = overCollection.meta.group;
            reorderedCollections = groupedCollections[newGroup] || [];
        } else {
            // Dropping at root level
            newGroup = null;
        }

        // Find old and new indices in the relevant array
        const oldIndex = reorderedCollections.findIndex(c => c.collection === active.id);
        const newIndex = reorderedCollections.findIndex(c => c.collection === over.id);

        if (oldIndex === -1) {
            // Item is moving to a new group
            reorderedCollections.push(activeCollection);
        } else {
            // Reorder within the same group
            reorderedCollections = arrayMove(reorderedCollections, oldIndex, newIndex);
        }

        // Update sort values for all affected collections
        const updates = reorderedCollections.map((collection, index) => ({
            collection: collection.collection,
            meta: {
                ...collection.meta,
                sort: index + 1,
                group: newGroup,
            },
        }));

        // Also update the dragged item's group if it changed
        const draggedUpdate = updates.find(u => u.collection === active.id);
        if (draggedUpdate) {
            try {
                await updateCollection.mutateAsync({
                    collection: activeCollection.collection,
                    payload: {
                        ...activeCollection.meta,
                        sort: draggedUpdate.meta.sort,
                        group: newGroup,
                    },
                });

                // Update all other collections in the group
                for (const update of updates) {
                    if (update.collection !== active.id) {
                        await updateCollection.mutateAsync({
                            collection: update.collection,
                            payload: update.meta,
                        });
                    }
                }
            } catch (error) {
                toast.error('Failed to update collection order');
            }
        }
    };

    const handleDragCancel = () => {
        setActiveId(null);
        setOverId(null);
    };

    if (isLoading) {
        return <CollectionsPageSkeleton />;
    }

    const hasCollections = rootCollections.length > 0 || Object.keys(groupedCollections).length > 0;
    const activeCollection = activeId ? sortedCollections.find(c => c.collection === activeId) : null;

    return (
        <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <Database className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-xl font-semibold text-foreground">Data Model</h1>
                        <p className="text-sm text-muted-foreground">
                            Configure collections, fields, and relationships
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search collection..."
                            className="w-[200px] pl-9"
                        />
                    </div>

                    {/* Create Folder */}
                    <Button
                        variant="outline"
                        onClick={() => {
                            setEditingFolder(null);
                            setShowFolderDialog(true);
                        }}
                        title="Create Folder"
                    >
                        <FolderPlus className="h-4 w-4" />
                    </Button>

                    {/* Create Collection */}
                    <Button onClick={() => setShowNewCollection(true)} title="Create Collection">
                        <Plus className="mr-2 h-4 w-4" />
                        Create Collection
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
                {!hasCollections ? (
                    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16">
                        <Database className="mb-4 h-12 w-12 text-muted-foreground" />
                        <h3 className="mb-2 text-lg font-medium text-foreground">No Collections</h3>
                        <p className="mb-4 text-muted-foreground">
                            Create your first collection to start building your data model
                        </p>
                        <Button onClick={() => setShowNewCollection(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Collection
                        </Button>
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                    >
                        <SortableContext
                            items={rootCollections.map(c => c.collection)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="space-y-1">
                                {/* Root Collections */}
                                {rootCollections.map((collection) => (
                                    <DraggableCollectionItem
                                        key={collection.collection}
                                        collection={collection}
                                        onDelete={() => setDeleteTarget(collection)}
                                        onEditFolder={handleEditFolder}
                                        onRenameFolder={handleRenameFolder}
                                        onToggleHidden={() => handleToggleHidden(collection)}
                                        childCollections={groupedCollections[collection.collection] || []}
                                        isExpanded={expandedGroups.has(collection.collection)}
                                        onToggleExpand={() => toggleGroup(collection.collection)}
                                        isOver={overId === collection.collection}
                                        groupedCollections={groupedCollections}
                                        expandedGroups={expandedGroups}
                                        onToggleGroup={toggleGroup}
                                        onDeleteChild={setDeleteTarget}
                                    />
                                ))}
                            </div>
                        </SortableContext>

                        <DragOverlay>
                            {activeCollection && (
                                <div className="rounded-lg border border-primary bg-background p-3 shadow-lg">
                                    <div className="flex items-center gap-3">
                                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                                        {activeCollection.schema ? (
                                            <Database className="h-4 w-4" />
                                        ) : (
                                            <Folder className="h-4 w-4" />
                                        )}
                                        <span className="font-medium">{activeCollection.collection}</span>
                                    </div>
                                </div>
                            )}
                        </DragOverlay>

                        {/* System Collections */}
                        {systemCollections.length > 0 && (
                            <div className="mt-8 border-t border-border pt-4">
                                <h3 className="mb-2 px-3 text-sm font-medium text-muted-foreground">
                                    System Collections
                                </h3>
                                {systemCollections.map((collection) => (
                                    <CollectionItemStatic
                                        key={collection.collection}
                                        collection={collection}
                                        isSystem
                                    />
                                ))}
                            </div>
                        )}

                        {/* Raw Tables */}
                        {tableCollections.length > 0 && (
                            <div className="mt-8 border-t border-border pt-4">
                                <h3 className="mb-2 px-3 text-sm font-medium text-muted-foreground">
                                    Database Tables
                                </h3>
                                {tableCollections.map((collection) => (
                                    <CollectionItemStatic
                                        key={collection.collection}
                                        collection={collection}
                                        isTable
                                    />
                                ))}
                            </div>
                        )}
                    </DndContext>
                )}
            </div>

            {/* Dialogs */}
            <NewCollectionSheet
                open={showNewCollection}
                onOpenChange={setShowNewCollection}
                onSuccess={handleCollectionCreated}
            />

            <CreateFolderDialog
                open={showFolderDialog}
                onOpenChange={setShowFolderDialog}
                folder={editingFolder}
                onSuccess={handleFolderCreated}
            />

            <RenameFolderDialog
                open={showRenameFolderDialog}
                onOpenChange={setShowRenameFolderDialog}
                folder={renamingFolder}
            />

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {deleteTarget?.schema ? 'Collection' : 'Folder'}</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{deleteTarget?.collection}"?
                            {deleteTarget?.schema && ' This will permanently delete the collection and all its data.'}
                            {!deleteTarget?.schema && ' All collections in this folder will be moved to the root level.'}
                            {' '}This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// Draggable Collection Item Component
interface DraggableCollectionItemProps {
    collection: Collection;
    onDelete: () => void;
    onEditFolder?: (folder: Collection) => void;
    onRenameFolder?: (folder: Collection) => void;
    onToggleHidden?: () => void;
    childCollections?: Collection[];
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    level?: number;
    isOver?: boolean;
    groupedCollections?: Record<string, Collection[]>;
    expandedGroups?: Set<string>;
    onToggleGroup?: (group: string) => void;
    onDeleteChild?: (collection: Collection) => void;
}

function DraggableCollectionItem({
    collection,
    onDelete,
    onEditFolder,
    onRenameFolder,
    onToggleHidden,
    childCollections = [],
    isExpanded = false,
    onToggleExpand,
    level = 0,
    isOver = false,
    groupedCollections = {},
    expandedGroups = new Set(),
    onToggleGroup,
    onDeleteChild,
}: DraggableCollectionItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: collection.collection });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const isFolder = !collection.schema;
    const hasChildren = childCollections.length > 0;

    return (
        <div ref={setNodeRef} style={style} className={cn(isOver && 'ring-2 ring-primary')}>
            <div
                className={cn(
                    'group flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-accent',
                    isDragging && 'opacity-50'
                )}
                style={{ paddingLeft: `${12 + level * 16}px` }}
            >
                {/* Drag Handle */}
                <button
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100"
                >
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                </button>

                {/* Expand/Collapse for folders with children */}
                {isFolder && hasChildren && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={onToggleExpand}
                    >
                        {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </Button>
                )}

                {/* Icon */}
                <div className="flex h-8 w-8 items-center justify-center rounded">
                    {isFolder ? (
                        collection.meta?.hidden ? (
                            <FolderOpen className="h-4 w-4 text-muted-foreground" />
                        ) : isExpanded ? (
                            <FolderOpen className="h-4 w-4 text-amber-500" />
                        ) : (
                            <Folder className="h-4 w-4 text-amber-500" />
                        )
                    ) : (
                        <Database className={cn(
                            "h-4 w-4",
                            collection.meta?.hidden ? "text-muted-foreground" : "text-blue-500"
                        )} />
                    )}
                </div>

                {/* Collection Name and Note */}
                <Link
                    to={isFolder ? '#' : `/admin/settings/data-model/${collection.collection}`}
                    className={cn(
                        "flex-1 min-w-0",
                        collection.meta?.hidden && "opacity-50"
                    )}
                    onClick={(e) => {
                        if (isFolder) {
                            e.preventDefault();
                            onToggleExpand?.();
                        }
                    }}
                >
                    <div className="font-medium text-foreground truncate">
                        {formatCollectionName(collection.collection)}
                    </div>
                    {collection.meta?.note && (
                        <div className="text-xs text-muted-foreground truncate">
                            {collection.meta.note}
                        </div>
                    )}
                </Link>

                {/* Actions Menu */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100"
                        >
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {isFolder && onEditFolder && (
                            <>
                                <DropdownMenuItem onClick={() => onEditFolder(collection)}>
                                    <Settings className="mr-2 h-4 w-4" />
                                    Edit Folder
                                </DropdownMenuItem>
                                {onRenameFolder && (
                                    <DropdownMenuItem onClick={() => onRenameFolder(collection)}>
                                        <FolderEdit className="mr-2 h-4 w-4" />
                                        Rename Folder
                                    </DropdownMenuItem>
                                )}
                            </>
                        )}
                        {!isFolder && (
                            <>
                                <DropdownMenuItem asChild>
                                    <Link to={`/admin/settings/data-model/${collection.collection}`}>
                                        <Settings className="mr-2 h-4 w-4" />
                                        Configure
                                    </Link>
                                </DropdownMenuItem>
                            </>
                        )}

                        {/* Hide/Show Option */}
                        <DropdownMenuItem onClick={onToggleHidden}>
                            {collection.meta?.hidden ? (
                                <>
                                    <Eye className="mr-2 h-4 w-4" />
                                    {isFolder ? 'Show Folder' : 'Show Collection'}
                                </>
                            ) : (
                                <>
                                    <EyeOff className="mr-2 h-4 w-4" />
                                    {isFolder ? 'Hide Folder' : 'Hide Collection'}
                                </>
                            )}
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={onDelete}
                            className="text-red-600 focus:text-red-600"
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete {isFolder ? 'Folder' : 'Collection'}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Nested Collections */}
            {isFolder && isExpanded && hasChildren && (
                <div className="ml-6 space-y-1 border-l-2 border-border pl-2">
                    {childCollections.map((child) => {
                        const isChildFolder = !child.schema;
                        const childChildren = isChildFolder && groupedCollections ? groupedCollections[child.collection] || [] : [];
                        return (
                            <DraggableCollectionItem
                                key={child.collection}
                                collection={child}
                                onDelete={() => onDeleteChild?.(child)}
                                onEditFolder={onEditFolder}
                                onRenameFolder={onRenameFolder}
                                onToggleHidden={onToggleHidden}
                                childCollections={childChildren}
                                isExpanded={expandedGroups?.has(child.collection)}
                                onToggleExpand={() => onToggleGroup?.(child.collection)}
                                level={level + 1}
                                groupedCollections={groupedCollections}
                                expandedGroups={expandedGroups}
                                onToggleGroup={onToggleGroup}
                                onDeleteChild={onDeleteChild}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Static Collection Item (for system collections and tables)
function CollectionItemStatic({
    collection,
    isSystem = false,
    isTable = false,
}: {
    collection: Collection;
    isSystem?: boolean;
    isTable?: boolean;
}) {
    return (
        <div
            className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-accent',
                (isSystem || isTable) && 'opacity-70'
            )}
        >
            <div className="flex h-8 w-8 items-center justify-center rounded bg-accent">
                <Database className="h-4 w-4 text-muted-foreground" />
            </div>

            <Link
                to={`/admin/settings/data-model/${collection.collection}`}
                className="flex-1"
            >
                <div className="font-medium text-foreground">
                    {collection.meta?.collection || collection.collection}
                </div>
                <div className="text-xs text-muted-foreground">
                    {collection.collection}
                </div>
            </Link>
        </div>
    );
}

function CollectionsPageSkeleton() {
    return (
        <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
                <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div>
                        <Skeleton className="mb-1 h-6 w-32" />
                        <Skeleton className="h-4 w-48" />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Skeleton className="h-10 w-[200px]" />
                    <Skeleton className="h-10 w-10" />
                    <Skeleton className="h-10 w-36" />
                </div>
            </div>
            <div className="flex-1 p-6">
                <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} className="h-14 w-full rounded-lg" />
                    ))}
                </div>
            </div>
        </div>
    );
}
